import { TFunction } from 'i18next';
import { compact, find, flattenDeep } from 'lodash';
import {
  EvseConnectorType,
  Maybe,
  UnifiedPermit,
  Vehicle,
  VehicleCountryCode,
  VehicleType,
} from 'types/generatedSchemaTypes';

export const MAX_LICENSE_PLATE_LENGTH = 12;

export const getVehicleLabel = (name: string, licensePlate: string): string => {
  if (name) {
    return `${name} (${licensePlate})`;
  }
  return licensePlate;
};
export const validateLicensePlate = (licensePlate?: string): boolean =>
  !!licensePlate &&
  licensePlate.trim().length >= 2 &&
  licensePlate.length <= MAX_LICENSE_PLATE_LENGTH;

export const isLicensePlateFormatValid = (
  licensePlate?: string,
  countryCode?: VehicleCountryCode
) => {
  switch (countryCode) {
    case 'S': {
      const regexp = new RegExp(/^([A-Z]{3})(\s?)(\d{2}[A-Z\d])$/); // 3 uppercase letters, optional space, 2 numbers then 1 number OR letter
      return regexp.test(licensePlate || '');
    }
    case 'FIN':
    case 'N':
    default:
      return true;
  }
};

export const getLicensePlateForAlwaysSelectVehicle = (
  latestVehicle: string,
  vehicles: Vehicle[]
) => {
  if (latestVehicle) {
    return latestVehicle;
  }

  if (vehicles?.length) {
    return vehicles[0].licensePlate?.plateText || '';
  }

  return '';
};

export const getLicensePlateForNonAlwaysSelectVehicle = (
  vehicles: Vehicle[]
) => {
  if (vehicles?.length === 1) {
    return vehicles[0].licensePlate?.plateText || '';
  }

  if (vehicles?.length > 1) {
    return '';
  }

  return '';
};

export const isVehicleLowEmission = (
  licensePlateBasedPermits: Maybe<UnifiedPermit[]> | undefined,
  { licensePlate: { plateText } }: Vehicle
): boolean => {
  const lowEmissionLicensePlateBasedPermits =
    licensePlateBasedPermits?.filter((permit) => permit.lowEmissionPermit) ||
    [];

  const lowEmissionLicensePlates = compact(
    flattenDeep(
      lowEmissionLicensePlateBasedPermits.map(
        (permit) => permit?.registrationPlates || []
      )
    )
  );

  return !!find(lowEmissionLicensePlates, { plateText });
};

export const colors = [
  '#FFFFFF',
  '#000000',
  '#BEBEBE',
  '#5B5B5B',
  '#E6E6E6',
  '#EA0606',
  '#044C8F',
  '#5AA7EE',
  '#6A4029',
  '#0C991B',
  '#D4BE9E',
  '#FC9E0F',
  '#FFDF39',
  '#8903C9',
];

export const vehicleIcons = [
  'vehicle-suv',
  'vehicle-sedan',
  'vehicle-hatchback',
  'vehicle-van',
  'vehicle-truck',
  'vehicle-cab',
  'vehicle-motorcycle',
  'vehicle-trailer',
];

export const vehicleCountries: VehicleCountryCode[] = [
  'A',
  'AL',
  'AND',
  'B',
  'BG',
  'BIH',
  'BY',
  'CH',
  'CZ',
  'D',
  'DK',
  'E',
  'EST',
  'F',
  'FL',
  'GB',
  'GR',
  'H',
  'HR',
  'I',
  'IRL',
  'IS',
  'L',
  'LR',
  'LT',
  'LV',
  'M',
  'MC',
  'MD',
  'MNE',
  'NL',
  'NMK',
  'P',
  'PL',
  'RO',
  'RSM',
  'RUS',
  'SK',
  'SLO',
  'SRB',
  'UA',
  'V',
];

export const mainVehicleCountries: (VehicleCountryCode | 'other')[] = [
  'FIN',
  'S',
  'N',
  'other',
];

export const vehicleChargerPlugTypes: EvseConnectorType[] = [
  'CHADEMO',
  'IEC_62196_T1',
  'IEC_62196_T1_COMBO',
  'IEC_62196_T2',
  'IEC_62196_T2_COMBO',
];

export const vehicleTypes: { id: VehicleType; icon: string }[] = [
  {
    id: 'REGULAR',
    icon: 'Car-Outlined',
  },
  {
    id: 'ELECTRIC',
    icon: 'Charging',
  },
];

const getNestedKeys = (obj: Record<string, any>, prefix = ''): string[] => {
  const paths: string[] = [];

  Object.entries(obj || {}).forEach(([key, value]) => {
    const currentPath = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      paths.push(...getNestedKeys(value, currentPath));
    } else {
      paths.push(currentPath);
    }
  });

  return paths;
};

export const getVehicleKeys = (vehicle: Vehicle | undefined): string[] => {
  if (!vehicle) {
    return [];
  }
  return getNestedKeys(vehicle);
};

export type VehicleCountryItem = {
  value: string;
  label: string;
  icon?: () => React.JSX.Element;
};

export const getVehicleCountryItems = (
  t: TFunction,
  IconComponent: (props: { country: string }) => React.JSX.Element,
  onlyMainCountries: boolean = false
): VehicleCountryItem[] => {
  const countriesToUse = onlyMainCountries
    ? mainVehicleCountries
    : vehicleCountries;

  return countriesToUse
    .map((country) => {
      return {
        label: t(`vehicle.form.country.${country}`),
        value: country,
        icon: () => IconComponent({ country }),
      };
    })
    .sort((a, b) => {
      // Keep "other" at the end
      if (a.value === 'other') {
        return 1;
      }
      if (b.value === 'other') {
        return -1;
      }
      return a.label.localeCompare(b.label);
    });
};

export const getSelectedCountryItem = (
  selectedCountry: string | undefined,
  mainCountryItems: VehicleCountryItem[],
  allCountryItems: VehicleCountryItem[]
): VehicleCountryItem | undefined => {
  if (!selectedCountry) {
    return undefined;
  }

  // Check if selected country is in main countries list
  const mainCountryMatch = mainCountryItems.find(
    (item) => item.value === selectedCountry
  );
  if (mainCountryMatch) {
    return mainCountryMatch;
  }

  // If not in main list, find it in all countries
  const allCountryMatch = allCountryItems.find(
    (item) => item.value === selectedCountry
  );

  if (allCountryMatch) {
    const otherItem = mainCountryItems.find((item) => item.value === 'other');
    if (otherItem) {
      return {
        ...otherItem,
        label: `${otherItem.label} (${allCountryMatch.label})`,
      };
    }
  }

  return undefined;
};
